// One Idempotency-Key per purchase ATTEMPT.
//
// The key is minted once when the employee confirms a purchase and reused verbatim
// on every retry of that same attempt (a timeout, a dropped connection, a double
// click). The server then answers a retry with the ORIGINAL order instead of buying
// again. A new key is minted only when the purchase itself changes (different
// product / recipient / amount / wallet) or after the attempt settled — i.e. the
// server accepted it, or definitively rejected it (4xx), so a later identical
// purchase is a genuinely new purchase.
import type { CreateOrderInput } from '../types';

export interface PurchaseAttempt {
  key: string;
  fingerprint: string;
}

export function newIdempotencyKey(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // RFC 4122 v4 fallback for older browsers.
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Stable identity of "what is being bought" — not of when. */
export function purchaseFingerprint(input: CreateOrderInput): string {
  return [
    input.countryCode,
    input.productType,
    input.productType === 'ELECTRICITY' ? '' : input.operator || '',
    input.savedRecipientId ? `saved:${input.savedRecipientId}` : input.recipient,
    input.payerMethodId ? `method:${input.payerMethodId}` : input.payerMsisdn,
    input.offerId || '',
    Math.round(input.amount),
    input.currency,
  ].join('|');
}

/**
 * Return the attempt to send: the pending one when this is a retry of the same
 * purchase, otherwise a fresh one with a new key.
 */
export function attemptFor(
  pending: PurchaseAttempt | null,
  input: CreateOrderInput,
  mint: () => string = newIdempotencyKey,
): PurchaseAttempt {
  const fingerprint = purchaseFingerprint(input);
  if (pending && pending.fingerprint === fingerprint) return pending;
  return { key: mint(), fingerprint };
}

/**
 * Whether a failed attempt should stay pending (so a retry reuses its key).
 * Keep it when the outcome is UNKNOWN to us — no response (network/timeout) or a
 * 5xx — because the server may have accepted it. Drop it on a definitive 4xx.
 */
export function keepAttemptAfterError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status == null) return true;
  return status >= 500;
}

// --- Surviving a refresh -------------------------------------------------------
//
// The attempt is kept in sessionStorage from the moment it is sent until the
// server answers. A refresh, a lost connection or a reopened tab therefore
// finds it again: the page warns that a purchase may already be in progress
// and, if the same purchase is submitted again, the SAME key is reused — the
// server answers with the original order instead of charging twice. Nothing is
// ever re-sent automatically.

const STORE_KEY = 'kp.everyday.pendingAttempt';

export interface StoredAttempt extends PurchaseAttempt {
  startedAt: number;
}

export function saveAttempt(a: PurchaseAttempt, now: number = Date.now()): void {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify({ key: a.key, fingerprint: a.fingerprint, startedAt: now }));
  } catch { /* storage unavailable — the in-memory attempt still protects double-clicks */ }
}

export function loadAttempt(maxAgeMs = 24 * 60 * 60 * 1000, now: number = Date.now()): StoredAttempt | null {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const a = JSON.parse(raw) as StoredAttempt;
    if (!a?.key || !a?.fingerprint || typeof a.startedAt !== 'number' || now - a.startedAt > maxAgeMs) {
      sessionStorage.removeItem(STORE_KEY);
      return null;
    }
    return a;
  } catch {
    return null;
  }
}

export function clearAttempt(): void {
  try { sessionStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
}
